import styled from "@emotion/styled";
import { motion } from "framer-motion";

const PrivacyContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
`;

const PrivacyTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: ${(props) => props.theme.colors.text};
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const PrivacyContent = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: ${(props) => props.theme.borderRadius.lg};
  padding: ${(props) => props.theme.spacing.lg};
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Section = styled.section`
  margin-bottom: ${(props) => props.theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  color: ${(props) => props.theme.colors.text};
  font-size: 1.8rem;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const Text = styled.p`
  color: ${(props) => props.theme.colors.textLight};
  line-height: 1.8;
  font-size: 1.1rem;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const List = styled.ul`
  list-style-type: disc;
  margin-left: ${(props) => props.theme.spacing.lg};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const ListItem = styled.li`
  color: ${(props) => props.theme.colors.textLight};
  line-height: 1.8;
  font-size: 1.1rem;
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const ContactInfo = styled.div`
  margin-top: ${(props) => props.theme.spacing.lg};
`;

export const PrivacyPolicy: React.FC = () => {
  return (
    <PrivacyContainer>
      <PrivacyTitle>Privacy Policy</PrivacyTitle>
      <PrivacyContent>
        <Text>
          <strong>Telazer LLC</strong> ("we", "us", "our") is committed to
          protecting your privacy. This Privacy Policy explains how your
          personal information is collected, used, and disclosed by Telazer LLC.
        </Text>

        <Section>
          <SectionTitle>Information We Collect</SectionTitle>
          <Text>
            We may collect information about you in a variety of ways. The
            information we may collect on the site includes:
          </Text>
          <List>
            <ListItem>
              <strong>Personal Data:</strong> Personally identifiable
              information, such as your name, shipping address, email address,
              and telephone number, and demographic information, such as your
              age, gender, hometown, and interests, that you voluntarily give to
              us.
            </ListItem>
            <ListItem>
              <strong>Derivative Data:</strong> Information our servers
              automatically collect when you access the site, such as your IP
              address, browser type, operating system, access times, and the
              pages you have viewed.
            </ListItem>
            <ListItem>
              <strong>Financial Data:</strong> Financial information, such as
              data related to your payment method that we may collect when you
              purchase our services.
            </ListItem>
            <ListItem>
              <strong>Mobile Device Data:</strong> Device information, such as
              your mobile device ID, model, and manufacturer, and information
              about the location of your device.
            </ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>Use of Your Information</SectionTitle>
          <Text>
            Having accurate information about you permits us to provide you with
            a smooth, efficient, and customized experience. We may use
            information collected about you via the site to:
          </Text>
          <List>
            <ListItem>Create and manage your account</ListItem>
            <ListItem>Process your transactions</ListItem>
            <ListItem>Manage your orders</ListItem>
            <ListItem>Improve and personalize our services</ListItem>
            <ListItem>Develop new products and features</ListItem>
            <ListItem>Send you administrative information</ListItem>
            <ListItem>Provide customer service</ListItem>
            <ListItem>Monitor and analyze trends</ListItem>
            <ListItem>Prevent fraudulent transactions</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>Disclosure of Your Information</SectionTitle>
          <Text>
            We may share information we have collected about you in certain
            situations:
          </Text>
          <List>
            <ListItem>
              <strong>By Law or to Protect Rights:</strong> If required by law
              or to protect rights
            </ListItem>
            <ListItem>
              <strong>Third-Party Service Providers:</strong> With service
              providers who assist our operations
            </ListItem>
            <ListItem>
              <strong>Business Transfers:</strong> In connection with any
              merger, sale, or acquisition
            </ListItem>
            <ListItem>
              <strong>With Your Consent:</strong> For any other purpose with
              your consent
            </ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>Security of Your Information</SectionTitle>
          <Text>
            We use administrative, technical, and physical security measures to
            protect your personal information. However, no security measures are
            perfect or impenetrable.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Policy for Children</SectionTitle>
          <Text>
            We do not knowingly solicit information from or market to children
            under the age of 13. If we learn that we have collected personal
            information from a child under age 13, we will delete that
            information.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Changes to This Privacy Policy</SectionTitle>
          <Text>
            We may update this Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy on our site.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Contact Us</SectionTitle>
          <Text>
            If you have questions about this Privacy Policy, please contact us
            at:
          </Text>
          <ContactInfo>
            <Text>
              <strong>Telazer LLC</strong>
              <br />
              4 Peddlers Row #1153
              <br />
              Newark, DE 19702 US
              <br />
              info@telazer.com
            </Text>
          </ContactInfo>
        </Section>
      </PrivacyContent>
    </PrivacyContainer>
  );
};
